<template>
  <div class="client-insight">
    <div class="go-back" @click="$router.go(-1)">
      <IconArrow />
      {{ $t('previous') }}
    </div>
    <div class="header">
      <div class="title">{{ $t('userActivities', { user: clientName }) }}</div>
      <InsightPeriodFilter v-model="period" />
    </div>

    <LineChart :labels="labels" :alert-data="alertData" :monitoring-data="monitoringData" class="chart" />
    <PieChart :labels="pieLabels" :data="pieChartData" class="pie chart" />
  </div>
</template>

<script>
import InsightPeriodFilter from '@/components/InsightPeriodFilter';
import LineChart from '@/components/LineChart';
import PieChart from '@/components/PieChart';

export default {
  components: { LineChart, InsightPeriodFilter, PieChart },

  data() {
    return {
      period: {
        text: this.$t('last6Months'),
        from: this.setPeriodStart(180),
      },
      data: [],
      alertData: [],
      monitoringData: [],
      pieChartData: [],
      labels: [],
      pieLabels: [],
      client: null,
    };
  },

  computed: {
    clientName() {
      return this.client ? this.client.name : '';
    },
  },

  watch: {
    period(period) {
      this.initChart(period);
    },
  },

  async created() {
    this.client = await this.$store.dispatch('getUsers', {
      id: this.$route.params.id,
    });
    this.data = await this.$store.dispatch('getClientInsight', this.$route.params.id);
    const pieInsight = await this.$store.dispatch('getClientPieChartInsight', this.$route.params.id);
    this.pieChartData = pieInsight.map(insight => insight.value);
    this.pieLabels = pieInsight.map(insight => `${insight.key} (${insight.value})`);
    this.initChart(this.period);
  },

  methods: {
    initChart(period) {
      const data = this.data ? this.data.filter(stats => (period ? new Date(stats.day).getTime() >= period.from : stats)) : [];
      if (period.text === this.$t('lastMonth')) {
        this.labels = data.map(stats => this.formatChartDate(stats.day));
        this.monitoringData = data.map(stats => stats.monitoringCount);
        this.alertData = data.map(stats => stats.alertCount);
      } else {
        this.groupDataByMonth(data);
      }
    },

    groupDataByMonth(data) {
      let months = [];
      data.forEach(item => {
        const month = new Date(item.day).getMonth() + 1;
        const year = new Date(item.day).getFullYear();
        let currentMonth = months.find(item => item && item.month === month && item.year === year);
        if (!currentMonth) {
          months.push({
            month: month,
            year: year,
            day: this.formatChartMonthDate(item.day),
            monitoringData: item.monitoringCount,
            alertData: item.alertCount,
          });
        } else {
          currentMonth.monitoringData += item.monitoringCount;
          currentMonth.alertData += item.alertCount;
        }
      });
      months = months.sort((a, b) => new Date(a.year, a.month, 1) - new Date(b.year, b.month, 1));
      this.labels = months.map(m => m.day);
      this.monitoringData = months.map(m => m.monitoringData);
      this.alertData = months.map(m => m.alertData);
    },

    formatChartDate(date) {
      const day = new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
      });
      let weekDay = new Date(date).toLocaleDateString('fr-FR', { weekday: 'short' }).slice(0, 1).toUpperCase();
      return `${weekDay}.${day}`;
    },

    formatChartMonthDate(date) {
      let month = new Date(date).toLocaleDateString('fr-FR', { month: 'short' }).slice(0, 3).toUpperCase();
      let year = new Date(date).getFullYear();
      return `${month}.${year}`;
    },

    setPeriodStart(days) {
      if (!days) return 0;
      const date = new Date(new Date().getTime() - 1000 * 3600 * 24 * days);
      this.resetHours(date);
      return date.getTime() || 0;
    },

    resetHours(date) {
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.client-insight {
  margin-top: 24px;
}

.go-back {
  display: flex;
  align-items: center;
  cursor: pointer;
  text-decoration: underline;
  transition: 0.3s;
  font-size: 14px;
  line-height: 14px;

  svg {
    margin-right: 6px;
    height: 14px;
  }

  &:hover {
    color: colors.$main-color;
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 24px;
}

.title {
  display: flex;
  align-items: flex-end;
  font-weight: 500;
  font-size: 20px;
  width: fit-content;
}

.chart {
  width: 80%;
  margin: 0 auto;
  margin: 60px auto;

  &.pie {
    width: 60%;
  }
}
</style>
